import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Security, LoginCallback, useOktaAuth } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';

import HRPortal from './components/HRPortal';
import AdminDashboard from './components/AdminDashboard';
import EmployeeSelfEval from './components/EmployeeSelfEval';
import ManagerReview from './components/ManagerReview';
import MidYearForm from './components/MidYearForm';
import MidYearManagerReview from './components/MidYearManagerReview';
import EmployeeHistory from './components/EmployeeHistory';
import OrgChart from './components/OrgChart';
import PolicyDocs from './components/PolicyDocs';
import LoginPage from './components/LoginPage';
import ProfileNotFound from './components/ProfileNotFound';
import Toast from './components/Toast';
import VersionBadge from './components/VersionBadge';
import { UserProvider, useUser } from './context/UserContext';
import { showInfo } from './lib/toast';
import { SESSION_EXPIRED } from './lib/api';

// ─── Okta configuration ───────────────────────────────────────────────────────
// ACTION REQUIRED: These values are pulled from environment variables.
// IT to add REACT_APP_OKTA_DOMAIN and REACT_APP_OKTA_CLIENT_ID
// to Azure Static Web App Application Settings.
//
// Refresh-token-based renewal is used instead of silent iframe renewal.
// Silent iframe renewal depends on third-party cookies to the Okta domain,
// which Safari (ITP), iOS, and modern Chrome/Firefox block — that's what
// caused the "stuck on LOADING" bug. With offline_access + refresh tokens
// the SDK renews via XHR, which has no third-party-cookie dependency.
//
// The Okta app registration MUST have the Refresh Token grant type enabled
// for this to work (xlight.okta.com → Applications → myhr → General →
// "Grant type" → check "Refresh Token", enable rotation).
const oktaAuth = new OktaAuth({
  issuer: `https://${process.env.REACT_APP_OKTA_DOMAIN}`,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI || window.location.origin + '/login/callback',
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true,
  tokenManager: {
    storage: 'localStorage',
    expireEarlySeconds: 30,
    autoRenew: true,
  },
  services: {
    autoRenew: true,
    autoRemove: true,
    syncStorage: true,
  },
});

// Defensive: if renewal ever fails (network error, refresh token revoked,
// clock skew, etc.) clear stale tokens and bounce to a fresh login instead
// of leaving the user stuck on LOADING.
oktaAuth.tokenManager.on('error', (err) => {
  console.warn('[okta] token manager error — forcing re-login:', err?.errorCode, err?.message);

  // Don't recurse into the redirect if we're already in the login flow.
  const path = window.location.pathname;
  if (path === '/login' || path === '/login/callback') {
    return;
  }

  oktaAuth.tokenManager.clear();
  oktaAuth.signInWithRedirect({
    originalUri: path + window.location.search,
  }).catch((redirectErr) => {
    console.error('[okta] signInWithRedirect failed after token error:', redirectErr);
  });
});

// ─── Protected route wrapper ──────────────────────────────────────────────────
const RequireAuth = ({ children, requiredRole }) => {
  const { authState } = useOktaAuth();
  const { employee, loading } = useUser();
  const location = useLocation();

  if (!authState || authState.isPending || loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#1e1e23', color: '#8888aa', fontSize: 13, letterSpacing: '0.06em' }}>LOADING...</div>;
  }

  // Stash the page the user was trying to reach so LoginPage can pass it
  // to Okta's signInWithRedirect, and restoreOriginalUri lands them back
  // there after the callback. Without this, every protected-route hit
  // dropped users on / regardless of intent.
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  if (!employee) return <ProfileNotFound />;

  // Role-based access
  if (requiredRole === 'Manager' && !['Manager', 'Admin'].includes(employee.role)) {
    return <Navigate to="/" />;
  }
  if (requiredRole === 'Admin' && employee.role !== 'Admin') {
    return <Navigate to="/" />;
  }

  return children;
};

// ─── App routes ───────────────────────────────────────────────────────────────
const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/login/callback" element={<LoginCallback />} />

    {/* Employee routes */}
    <Route path="/" element={<RequireAuth><HRPortal /></RequireAuth>} />
    <Route path="/reviews/annual" element={<RequireAuth><EmployeeSelfEval /></RequireAuth>} />
    <Route path="/reviews/midyear" element={<RequireAuth><MidYearForm /></RequireAuth>} />
    <Route path="/reviews/history" element={<RequireAuth><EmployeeHistory /></RequireAuth>} />
    <Route path="/org-chart" element={<RequireAuth><OrgChart /></RequireAuth>} />
    <Route path="/policies" element={<RequireAuth><PolicyDocs /></RequireAuth>} />

    {/* Manager routes */}
    <Route path="/manager/annual" element={<RequireAuth requiredRole="Manager"><ManagerReview /></RequireAuth>} />
    <Route path="/manager/midyear" element={<RequireAuth requiredRole="Manager"><MidYearManagerReview /></RequireAuth>} />

    {/* Admin routes */}
    <Route path="/admin" element={<RequireAuth requiredRole="Admin"><AdminDashboard /></RequireAuth>} />

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" />} />
  </Routes>
);

// Listens for the session-expired event fired by api.js when PostgREST
// rejects a JWT (almost always: token expired). Shows a clear message
// and signs the user out so Okta redirects them through fresh auth.
//
// Idempotent: concurrent 401s would otherwise trigger many sign-outs.
// The `triggered` ref ensures we only run the flow once per mount.
const SessionGuard = () => {
  const { oktaAuth } = useOktaAuth();
  const triggered = useRef(false);
  const timeoutHandle = useRef(null);

  useEffect(() => {
    const loginUrl = window.location.origin + '/login';

    const onExpired = () => {
      if (triggered.current) return;
      triggered.current = true;

      showInfo('Your session expired. Signing you back in…');
      // brief delay so the toast renders before the redirect
      timeoutHandle.current = setTimeout(async () => {
        try {
          await oktaAuth.signOut({ postLogoutRedirectUri: loginUrl });
        } catch (err) {
          // Network failure, popup blocker, etc. Hard-navigate as a
          // fallback so the user isn't stranded on a broken session.
          console.error('signOut failed, falling back to navigation:', err);
          window.location.replace(loginUrl);
        }
      }, 1500);
    };

    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED, onExpired);
      if (timeoutHandle.current) clearTimeout(timeoutHandle.current);
    };
  }, [oktaAuth]);
  return null;
};

export default function App() {
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(originalUri || '/');
  };

  return (
    <BrowserRouter>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <UserProvider>
          <SessionGuard />
          <AppRoutes />
          <Toast />
          <VersionBadge />
        </UserProvider>
      </Security>
    </BrowserRouter>
  );
}
